import { useHistoryUpdateHomepass } from "@/api/history-update-homepass";
import { useHPTARs, useTechnicalChange } from "@/api/hptar";
import { FieldFile } from "@/components/form/field-file";
import { FormItem, FormLabel } from "@/components/form/form";
import { FormContainer } from "@/components/form/form-container";
import { SubmitButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/utils/helper";
import { formatIsoAsWib } from "@/utils/helper/formatDate";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";
import useValidateCableDistribution from "./hooks/useValidateCableDistribution";

const cableDistributionFormSchema = z.object({
  hasTechnicalChange: z.boolean(),
  fileCableDistribution: z.instanceof(FileList).superRefine((files, ctx) => {
    if (files.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "File is required",
      });
    }

    // validation type
    if (files.length > 0 && files?.[0].name.endsWith(".kmz") === false) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "File must be a .kmz file",
      });
    }
  }),
});

export type CableDistributionFormSchema = z.infer<
  typeof cableDistributionFormSchema
>;

export default function CableDistributionField({
  isReadonly = false,
}: {
  isReadonly?: boolean;
}) {
  const { id } = useParams();
  const { data } = useHistoryUpdateHomepass({ rfsId: id });
  const { data: hptars } = useHPTARs({ rfsId: id! });
  const { mutate: technicalChange } = useTechnicalChange();

  const hptar = hptars?.[0];
  const lastUpdated = hptar?.lastUpdateCableSlingwireQspan;
  const hasTechnicalChange = hptar?.hasTechnicalChange;
  const filePathExisting = data?.[0]?.fileCableSlingwireQspan ?? undefined;
  const defaultFileName = filePathExisting
    ? filePathExisting.split("/").pop() || "file.kmz"
    : undefined;

  const form = useForm<CableDistributionFormSchema>({
    defaultValues: {
      hasTechnicalChange: hasTechnicalChange ?? false,
    },
  });

  function onChangeHasTechnicalChange(checked: boolean) {
    if (id && hptar?.id) {
      technicalChange({
        id: hptar?.id,
        hasTechnicalChange: checked,
      });
    }
  }

  const fileCableDistribution = form.watch("fileCableDistribution");
  const { handleSubmit } = useValidateCableDistribution({ id });

  return (
    <FormContainer form={form} onSubmit={handleSubmit} className="space-y-4">
      <FormItem className={cn("flex flex-row items-start space-x-3 space-y-0")}>
        <Checkbox
          checked={hasTechnicalChange ?? false}
          onCheckedChange={(checked) => onChangeHasTechnicalChange(!!checked)}
          id={"hasTechnicalChange"}
          disabled={isReadonly}
        />

        <FormLabel className="font-normal" htmlFor={"hasTechnicalChange"}>
          Check if there are changes to the CABLE DISTRIBUTION, SLINGWIRE,
          QSPAN, POLE (.kmz)
        </FormLabel>
      </FormItem>

      {hasTechnicalChange && (
        <>
          <FieldFile
            label="Upload CABLE DISTRIBUTION, SLINGWIRE, QSPAN (.kmz)"
            name="fileCableDistribution"
            accept=".kmz"
            defaultName={defaultFileName}
            canDownloadExistingFile={Boolean(filePathExisting)}
            pathExistingFile={filePathExisting}
            isDisabled={isReadonly}
          />

          <div className="text-sm">
            Last Update:{" "}
            <b>{lastUpdated ? formatIsoAsWib(lastUpdated) : "-"}</b>
          </div>

          <SubmitButton
            disabled={
              !fileCableDistribution ||
              fileCableDistribution.length === 0 ||
              isReadonly
            }
          />
        </>
      )}
    </FormContainer>
  );
}
